import { createAuthClient } from 'better-auth/client'
import { emailOTPClient } from 'better-auth/client/plugins'
import { normalizeEmail } from './emailAddress'
import { revokeOtherSessions } from './revokeOtherSessions'

// better-auth's client requires an absolute URL (a bare path like '/api/auth' throws
// "Invalid base URL" — it validates via `new URL(...)`), so build it from the page origin
// rather than hardcoding a host.
//
// Two ways in: Google, and a 6-digit code emailed to the address the user types.
// Neither stores a password; the OTP plugin is the one that needs no third-party account.
export const authClient = createAuthClient({
  baseURL: `${window.location.origin}/api/auth`,
  plugins: [emailOTPClient()],
})

/** There's no anonymous/guest mode — the app must find a real session before loading anything
 * (see App.tsx's sign-in gate / LoginScreen). `data` is null when signed out.
 *
 * Dev-only fallback: when the server has `DEV_AUTH_BYPASS` enabled it authenticates `/api/*` as a
 * dev user even without a signed-in session (see server/auth.ts), so the app can be driven headless
 * for build checks. We detect that purely from the server's behaviour — no client flag needed —
 * by asking `/api/me`: it returns 200 with the bypass user when bypass is on, and the real 401
 * otherwise (production always 401s here, so this fallback is inert there). */
export async function getSession() {
  const result = await authClient.getSession()
  if (result.data) return result

  try {
    const res = await fetch(`${window.location.origin}/api/me`, { credentials: 'include' })
    if (res.ok) {
      const me = (await res.json()) as { userId: string; email: string }
      return { ...result, data: { user: { id: me.userId, email: me.email } } }
    }
  } catch {
    // Network error — fall through to the signed-out result below.
  }

  return result
}

/** Navigates the browser to Google's consent screen; better-auth's client auto-redirects when
 * the server responds with a redirect URL (see server/betterAuth.ts). On success Google sends
 * the browser back to `callbackURL`, which reloads the app with the signed-in session. */
export function signInWithGoogle() {
  return authClient.signIn.social({ provider: 'google', callbackURL: window.location.origin })
}

/** Mails a 6-digit sign-in code to `email`, creating no account and no session yet. Resolves with
 * `{ error }` — the server refuses with 429 when the address (or the IP) has asked too often, and
 * with a 500 when the mail could not actually be handed to the provider. */
export function sendLoginCode(email: string) {
  return authClient.emailOtp.sendVerificationOtp({
    email: normalizeEmail(email),
    type: 'sign-in',
  })
}

/** Exchanges the emailed code for a session. First code for an address creates the account (same
 * destination as Google: a better-auth `user` row, our `users` row, Global league membership). */
export function signInWithCode(email: string, code: string) {
  return authClient.signIn.emailOtp({ email: normalizeEmail(email), otp: code.trim() })
}

/**
 * Signs out — on **every** device, not just this one.
 *
 * The decision behind that: "log out" is the phrase people already reach for when a phone goes
 * missing, and a second control next to it saying "…but everywhere" is a distinction nobody should
 * have to make while worried. So the ordinary button carries the whole meaning.
 *
 * The cost, stated plainly: logging out on a borrowed laptop also signs you out on your own phone.
 * For an app you sign into with one tap that is an inconvenience, not a loss.
 *
 * Revocation first, then better-auth's own sign-out clears this browser's cookie. If the revoke
 * call fails we still sign out locally — leaving someone apparently signed in on the device in
 * front of them because a background call failed is the worse outcome.
 */
export async function logout() {
  try {
    await revokeOtherSessions()
  } catch {
    // Deliberately swallowed: see above.
  }
  return authClient.signOut()
}
